import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { usePermissions } from "context/permissionContenxt";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import "./MhdProfile.css";

const dateFormatter = new Intl.DateTimeFormat("el-GR");
const currencyFormatter = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
});

export const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

const formatCurrency = (value) => currencyFormatter.format(Number(value ?? 0));
const formatPercentage = (value) => new Intl.NumberFormat("el-GR", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value ?? 0));

const Value = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value === 0 ? 0 : value || "-"}
    </Typography>
  </Box>
);

export const ProfileBreadcrumbs = ({ profile, apartment, childName }) => {
  const apartmentPath = `/govnr/mhds/${profile?.applicationId}/apartments/${apartment?.id}`;
  const apartmentLabel = apartment && (apartment.position
    ? `Δ${apartment.position} · ${apartment.address || apartment.name}`
    : apartment.address || apartment.name || "Διαμέρισμα");

  return <Breadcrumbs aria-label="Διαδρομή πλοήγησης" sx={{ mb: 1.5, fontSize: 13 }}>
    <Link component={RouterLink} to="/govnr/apartments" underline="hover" color="inherit">
      Διαμερίσματα ΜΗΔ
    </Link>
    {apartment || childName ? (
      <Link
        component={RouterLink}
        to={`/govnr/mhds/${profile?.applicationId}`}
        underline="hover"
        color="inherit"
      >
        {profile?.name || "ΜΗΔ"}
      </Link>
    ) : (
      <Typography color="text.primary" fontSize="inherit" fontWeight={600}>
        {profile?.name || "ΜΗΔ"}
      </Typography>
    )}
    {apartment && childName && (
      <Link component={RouterLink} to={apartmentPath} underline="hover" color="inherit">
        {apartmentLabel}
      </Link>
    )}
    {(apartment || childName) && (
      <Typography color="text.primary" fontSize="inherit" fontWeight={600}>
        {childName || apartmentLabel}
      </Typography>
    )}
  </Breadcrumbs>
};

export const ProfileHeader = ({ profile, apartment, onBack }) => {
  const apartments = profile?.apartments || [];
  const capacity = apartments.reduce((total, item) => total + item.capacity, 0);
  const residents = apartments.reduce((total, item) => total + item.currentBeneficiaries, 0);

  return (
    <>
      <ProfileBreadcrumbs profile={profile} apartment={apartment} />
      <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden", borderColor: "divider" }}>
        <Box sx={{ height: 4, bgcolor: "primary.main" }} />
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.75 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography component="h1" variant="h5" fontWeight={700}>
                  {apartment?.name || profile?.name || "ΜΗΔ"}
                </Typography>
                {profile?.status && <Chip size="small" color="primary" variant="outlined" label={profile.status} />}
              </Stack>
              {apartment && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {profile?.name}
                </Typography>
              )}
            </Box>
            <Button size="small" variant="outlined" startIcon={<ArrowBackOutlined />} onClick={onBack} sx={{ alignSelf: "flex-start", whiteSpace: "nowrap" }}>
              Επιστροφή
            </Button>
          </Stack>
          <Stack direction="row" spacing={{ xs: 1.5, md: 3 }} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Value label="Φορέας" value={profile?.providerName} />
            <Value label="ΑΦΜ φορέα" value={profile?.providerAfm} />
            <Value label="Έναρξη λειτουργίας" value={formatDate(profile?.operationDate)} />
            {apartment && <Value label="Διεύθυνση" value={apartment.address} />}
          </Stack>
        </Box>
        {!apartment && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 4 }, px: { xs: 1.5, md: 2.5 }, py: 1, bgcolor: "#f3f8fd", borderTop: 1, borderColor: "divider" }}>
            <Value label="Διαμερίσματα" value={apartments.length.toLocaleString("el-GR")} />
            <Value label="Θέσεις" value={capacity.toLocaleString("el-GR")} />
            <Value label="Διαμένοντες" value={residents.toLocaleString("el-GR")} />
            <Value label="Προσωπικό" value={(profile?.employees?.length || 0).toLocaleString("el-GR")} />
          </Box>
        )}
      </Paper>
    </>
  );
};

export const ExpenseSummary = ({ summary, onOpen, showHeader = true }) => {
  if (!summary) return null;

  return (
    <Card variant={showHeader ? "outlined" : undefined} sx={showHeader ? undefined : { boxShadow: "none" }}>
      <CardContent sx={showHeader ? undefined : { p: "0 !important" }}>
        {showHeader && <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Typography variant="h5" fontWeight={700}>
            Συγκεντρωτικά δαπανών
          </Typography>
          <Button onClick={onOpen}>Προβολή αναλυτικών δαπανών</Button>
        </Stack>}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 1.5,
            mt: 2,
          }}
        >
          {summary.budgetAmount !== null && summary.budgetAmount !== undefined && (
            <Value label="Π/Υ" value={formatCurrency(summary.budgetAmount)} />
          )}
          <Value label="Πλήθος δαπανών" value={summary.expensesCount?.toLocaleString("el-GR")} />
          <Value label="Ποσό δαπανών" value={formatCurrency(summary.expensesAmount)} />
          <Value label="Εγκεκριμένο ποσό" value={formatCurrency(summary.approvedAmount)} />
          {summary.paidAmount !== null && summary.paidAmount !== undefined && (
            <Value label="Πληρωμένο ποσό" value={formatCurrency(summary.paidAmount)} />
          )}
          {summary.availableAmount !== null && summary.availableAmount !== undefined && (
            <Value label="Υπόλοιπο" value={formatCurrency(summary.availableAmount)} />
          )}
          {summary.consumptionPerc !== null && summary.consumptionPerc !== undefined && (
            <Value label="Ανάλωση %" value={formatPercentage(summary.consumptionPerc)} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const BeneficiariesTable = ({
  beneficiaries = [],
  filter,
  applicationId,
  apartmentId,
  apartment,
  mhdName,
  returnToApartment = false,
}) => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canViewBeneficiaryProfile = permissions.can("View", "MHD_BeneficiaryProfile");
  const rows = beneficiaries.filter((beneficiary) => {
    if (filter === "current") return beneficiary.isCurrent;
    if (filter === "past") return !beneficiary.isCurrent;
    return true;
  });

  if (!rows.length) {
    return <Alert severity="info">Δεν υπάρχουν εγγραφές για το επιλεγμένο φίλτρο.</Alert>;
  }

  return (
    <FlexGrid
      className="mhd-profile-grid"
      itemsSource={rows.map((item) => ({ ...item, fullName: `${item.lastName} ${item.firstName}` }))}
      isReadOnly
      autoClipboard
      selectionMode="CellRange"
      headersVisibility="All"
      alternatingRowStep={0}
      style={{ height: Math.min(390, 52 + rows.length * 32) }}
      initialized={(grid) => {
        grid.rows.defaultSize = 32;
        grid.columnHeaders.rows.defaultSize = 34;
        grid.rowHeaders.columns.defaultSize = 28;
      }}
    >
      <FlexGridFilter />
      <FlexGridColumn binding="fullName" header="Ονοματεπώνυμο" width="2*" minWidth={185}>
        <FlexGridCellTemplate cellType="Cell" template={({ item }) => {
          if (!canViewBeneficiaryProfile) {
            return <Typography fontWeight={600}>{item.fullName}</Typography>;
          }

          const href = `/govnr/mhds/${applicationId}/beneficiaries/${item.childId}?apartmentId=${apartmentId}${returnToApartment ? "&from=apartment" : ""}`;
          return (
            <Link
              href={href}
              underline="hover"
              fontWeight={600}
              onClick={(event) => {
                if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                  event.preventDefault();
                  navigate(href, {
                    state: {
                      mhdBreadcrumb: {
                        applicationId,
                        childId: item.childId,
                        mhdName,
                        apartment: {
                          id: apartment.id,
                          position: apartment.position,
                          name: apartment.name,
                          address: apartment.address,
                        },
                      },
                    },
                  });
                }
              }}
            >
              {item.fullName}
            </Link>
          );
        }} />
      </FlexGridColumn>
      <FlexGridColumn binding="gender" header="Φύλο" width={90} />
      <FlexGridColumn binding="dateOfBirth" header="Ημ/νία γέννησης" width={145}>
        <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDate(item.dateOfBirth)} />
      </FlexGridColumn>
      <FlexGridColumn binding="amka" header="ΑΜΚΑ" width={135} />
      <FlexGridColumn binding="dateFrom" header="Από" width={120}>
        <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDate(item.dateFrom)} />
      </FlexGridColumn>
      <FlexGridColumn binding="dateTo" header="Έως" width={120}>
        <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDate(item.dateTo)} />
      </FlexGridColumn>
      <FlexGridColumn binding="isCurrent" header="Κατάσταση" width={155}>
        <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
          <Chip size="small" color={item.isCurrent ? "success" : "default"} label={item.isCurrent ? "Τρέχων" : "Παλαιότερη διαμονή"} />
        )} />
      </FlexGridColumn>
    </FlexGrid>
  );
};

export const BeneficiaryFilter = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 210 }}>
    <InputLabel id="beneficiary-filter-label">Ωφελούμενοι</InputLabel>
    <Select
      labelId="beneficiary-filter-label"
      value={value}
      label="Ωφελούμενοι"
      onChange={(event) => onChange(event.target.value)}
    >
      <MenuItem value="current">Τρέχοντες</MenuItem>
      <MenuItem value="past">Παλαιότερες διαμονές</MenuItem>
      <MenuItem value="all">Όλοι</MenuItem>
    </Select>
  </FormControl>
);

export const ApartmentCard = ({ apartment, applicationId, mhdName, filter, showOpen = true }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(!showOpen);
  const occupancyColor = apartment.currentBeneficiaries === 0
    ? "success"
    : apartment.currentBeneficiaries === apartment.capacity ? "error" : "warning";

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", borderColor: "divider" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 1.5, py: 1, bgcolor: "#f3f8fd", alignItems: { md: "center" } }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={700}>
              Δ{apartment.position} · {apartment.address || apartment.name}
            </Typography>
            {showOpen && (
              <Button
                size="small"
                endIcon={<ArrowForwardOutlined fontSize="small" />}
                onClick={() => navigate(`/govnr/mhds/${applicationId}/apartments/${apartment.id}`)}
              >
                Άνοιγμα
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {apartment.gender || "Όλα τα φύλα"} · {apartment.ageRange || "Χωρίς ηλικιακό περιορισμό"}
            {apartment.isForDisabled ? " · ΑμεΑ" : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`Θέσεις ${apartment.currentBeneficiaries}/${apartment.capacity}`}
            color={occupancyColor}
            variant="outlined"
            sx={{
              fontWeight: 600,
              ...(occupancyColor === "success" && {
                color: "#1b5e20",
                borderColor: "#2e7d32",
                bgcolor: "#edf7ee",
              }),
              ...(occupancyColor === "warning" && {
                color: "#8a4b00",
                borderColor: "#b66a00",
                bgcolor: "#fff7e6",
              }),
              ...(occupancyColor === "error" && {
                color: "#8b1e1e",
                borderColor: "#b60202",
                bgcolor: "#fdecec",
              }),
            }}
          />
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 180ms" }} />}
          >
            {expanded ? "Απόκρυψη ωφελούμενων" : "Ωφελούμενοι"}
          </Button>
        </Stack>
      </Stack>
      {expanded && (
        <Box sx={{ p: 0.75 }}>
          <BeneficiariesTable
            beneficiaries={apartment.beneficiaries}
            filter={filter}
            applicationId={applicationId}
            apartmentId={apartment.id}
            apartment={apartment}
            mhdName={mhdName}
            returnToApartment={!showOpen}
          />
        </Box>
      )}
    </Paper>
  );
};

export const SectionTitle = ({ children, action }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    spacing={1}
    sx={{ mb: 1.5 }}
  >
    <Typography variant="h5" fontWeight={700}>
      {children}
    </Typography>
    {action}
  </Stack>
);
